import { useCallback, useEffect, useMemo, useState } from 'react';

import { servingApi, WEIGHT_MODES } from '../../api';
import {
  Badge,
  Button,
  CopyButton,
  EmptyState,
  ErrorState,
  Loading,
  Modal,
  ProgressBar,
  Select,
  TextInput,
} from '../../components/common';
import { useAuth } from '../../context/AuthContext';
import { useModel } from '../../context/ModelContext';
import { useToast } from '../../context/ToastContext';
import { relativeTime } from '../../utils/format';
import '../pages.css';

/**
 * Model serving (vLLM): what is on the GPU, and the policy that decides what may be.
 *
 * Everyone can read this page — "which models can I use right now?" is a fair
 * question for anyone. Only an administrator can change it, and the API enforces
 * that; the disabled controls here are a courtesy.
 */

const POLL_MS = 15000;
const BUSY_POLL_MS = 4000;

const STATE_TONES = {
  ready: 'ok',
  starting: 'warn',
  pending: 'warn',
  draining: 'warn',
  failed: 'danger',
  unknown: 'danger',
  stopped: 'default',
};

const DEFAULT_WEIGHT_OPTIONS = [
  {
    value: 'auto',
    label: 'Auto (recommended)',
    hint: 'NF4-faithful for versions fine-tuned with 4-bit QLoRA, as published for everything else.',
  },
  ...WEIGHT_MODES,
];

function percent(value) {
  return `${Math.round((Number(value) || 0) * 100)}%`;
}

function weightsLabel(mode) {
  return WEIGHT_MODES.find((option) => option.value === mode)?.label || mode || '—';
}

export function ServingTab() {
  const toast = useToast();
  const { isAdmin } = useAuth();
  const { readyVersions, versions, refreshVersions, refreshStatus } = useModel();

  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loadVersionId, setLoadVersionId] = useState('');
  const [loadingModel, setLoadingModel] = useState(false);
  const [unloading, setUnloading] = useState(null);
  const [confirmUnload, setConfirmUnload] = useState(null);
  const [preparation, setPreparation] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const payload = await servingApi.status();
      setStatus(payload);
      setError(null);
      return payload;
    } catch (cause) {
      setError(cause);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const servers = useMemo(() => status?.servers || [], [status]);
  const busy = servers.some((server) => ['starting', 'draining', 'pending'].includes(server.state));
  useEffect(() => {
    const timer = setInterval(refresh, busy ? BUSY_POLL_MS : POLL_MS);
    return () => clearInterval(timer);
  }, [busy, refresh]);

  // The editable copy of the policy, reset whenever the saved one changes.
  const policy = status?.policy;
  useEffect(() => {
    if (!policy) return;
    setDraft({
      multi_model: Boolean(policy.multi_model),
      max_models: policy.max_models,
      gpu_budget: policy.gpu_budget,
      default_weights: policy.default_weights,
    });
  }, [policy]);

  const dirty =
    draft &&
    policy &&
    (draft.multi_model !== policy.multi_model ||
      Number(draft.max_models) !== policy.max_models ||
      Number(draft.gpu_budget) !== policy.gpu_budget ||
      draft.default_weights !== policy.default_weights);

  const savePolicy = useCallback(async () => {
    if (!draft) return;
    setSaving(true);
    try {
      await servingApi.updatePolicy({
        multi_model: draft.multi_model,
        max_models: Number(draft.max_models),
        gpu_budget: Number(draft.gpu_budget),
        default_weights: draft.default_weights,
      });
      await Promise.all([refresh(), refreshVersions(), refreshStatus()]);
      toast.success(
        'Serving policy saved',
        draft.multi_model
          ? `Up to ${draft.max_models} base models, ${percent(draft.gpu_budget)} of the GPU between them.`
          : `One base model at a time, with ${percent(draft.gpu_budget)} of the GPU.`,
      );
    } catch (cause) {
      toast.error(cause, { title: 'Could not save the serving policy' });
    } finally {
      setSaving(false);
    }
  }, [draft, refresh, refreshStatus, refreshVersions, toast]);

  const loadModel = useCallback(async () => {
    if (!loadVersionId) return;
    setLoadingModel(true);
    setPreparation(null);
    try {
      const result = await servingApi.load(loadVersionId);
      await Promise.all([refresh(), refreshVersions(), refreshStatus()]);
      if (result.already_loaded) {
        toast.info('Already loaded', `${result.model_source} is serving ${loadVersionId}.`);
      } else {
        toast.success(
          'Loading base model',
          `${result.model_source} (${weightsLabel(result.weights_mode)}) with ${percent(
            result.gpu_fraction,
          )} of the GPU. Ready in one to three minutes.${
            result.stopped?.length ? ` Replaced ${result.stopped.join(', ')}.` : ''
          }`,
        );
      }
    } catch (cause) {
      if (cause.code === 'serving_not_prepared') setPreparation(cause);
      else toast.error(cause, { title: 'Could not load the model' });
    } finally {
      setLoadingModel(false);
    }
  }, [loadVersionId, refresh, refreshStatus, refreshVersions, toast]);

  const unload = useCallback(
    async (server) => {
      setConfirmUnload(null);
      setUnloading(server.key);
      try {
        await servingApi.unload(server.key);
        await Promise.all([refresh(), refreshVersions(), refreshStatus()]);
        toast.success(
          'Unloading',
          `${server.model_source || server.key}: in-flight requests finish first, then its GPU memory is released.`,
        );
      } catch (cause) {
        toast.error(cause, { title: 'Could not unload the model' });
      } finally {
        setUnloading(null);
      }
    },
    [refresh, refreshStatus, refreshVersions, toast],
  );

  if (loading && !status) return <Loading label="Reading model serving…" large />;
  if (error && !status) return <ErrorState error={error} onRetry={refresh} />;

  if (status?.backend !== 'vllm') {
    return (
      <EmptyState glyph="◫" title="Model serving is not enabled on this deployment">
        {status?.message}
        <pre className="mono serving__code">{`# .env
INFERENCE_BACKEND=vllm
WORKER_ROLE=embed
MODEL_SERVER_API_KEY=<openssl rand -base64 32>

docker compose up -d --build`}</pre>
        See docs/DEPLOYMENT.md, “Serving many users with vLLM”.
      </EmptyState>
    );
  }

  const budget = status.budget || {};
  const gpu = status.gpu || {};
  const admission = status.admission || {};
  const reachable = status.model_server?.reachable;
  const slots = draft?.multi_model ? Math.max(1, Number(draft.max_models) || 1) : 1;
  const sharePerModel = (Number(draft?.gpu_budget) || 0) / slots;
  const gpuTotalGb = gpu.memory_total_mb ? gpu.memory_total_mb / 1024 : null;
  const selectedVersion = versions.find((version) => version.id === loadVersionId);
  const loadedDesired = servers.filter((server) => server.desired);
  const weightsHint = DEFAULT_WEIGHT_OPTIONS.find(
    (option) => option.value === draft?.default_weights,
  )?.hint;

  return (
    <div className="col" style={{ gap: 16, maxWidth: 980 }}>
      <div className="row">
        <Button size="sm" variant="ghost" onClick={refresh}>
          Refresh
        </Button>
        {reachable ? (
          <Badge tone="ok">model server online</Badge>
        ) : (
          <Badge tone="danger">model server offline</Badge>
        )}
        <Badge>{status.policy?.mode === 'multi' ? 'multi-model' : 'single-model'}</Badge>
        {!isAdmin && <span className="xs muted">Read-only: an administrator changes these.</span>}
      </div>

      {!reachable && (
        <section className="card card--pad">
          <strong>The model server is not reachable</strong>
          <div className="small muted" style={{ marginTop: 6 }}>
            {status.model_server?.error}
          </div>
          <pre className="mono serving__code">{`docker compose ps modelserver
docker compose logs --tail=100 modelserver
docker compose up -d modelserver`}</pre>
        </section>
      )}

      {/* ------------------------------------------------------------ policy */}
      <section className="card card--pad">
        <div className="section-label" style={{ marginBottom: 10 }}>
          Serving policy
        </div>
        {draft && (
          <div className="editor">
            <fieldset className="serving__modes" disabled={!isAdmin}>
              <legend className="field__label">MODELS ON THE GPU</legend>
              <label className={`serving__mode ${!draft.multi_model ? 'serving__mode--on' : ''}`}>
                <input
                  type="radio"
                  name="serving-mode"
                  checked={!draft.multi_model}
                  onChange={() => setDraft({ ...draft, multi_model: false })}
                />
                <span>
                  <strong>One model (strict)</strong>
                  <span className="xs muted">
                    Loading another base model replaces the current one. It gets the whole budget —
                    the most capacity per model.
                  </span>
                </span>
              </label>
              <label className={`serving__mode ${draft.multi_model ? 'serving__mode--on' : ''}`}>
                <input
                  type="radio"
                  name="serving-mode"
                  checked={draft.multi_model}
                  onChange={() => setDraft({ ...draft, multi_model: true })}
                />
                <span>
                  <strong>Several models</strong>
                  <span className="xs muted">
                    Up to the limit below, sharing the budget equally. When every slot is taken a
                    load is refused until someone unloads — nothing running is interrupted.
                  </span>
                </span>
              </label>
            </fieldset>

            <div className="editor__row">
              <Select
                label="MAXIMUM MODELS"
                value={String(draft.max_models)}
                disabled={!isAdmin || !draft.multi_model}
                onChange={(event) => setDraft({ ...draft, max_models: Number(event.target.value) })}
                options={[1, 2, 3, 4].map((count) => ({ value: String(count), label: count }))}
                hint="Only used with several models."
              />
              <TextInput
                label="GPU BUDGET"
                type="number"
                min={0.1}
                max={0.95}
                step={0.05}
                disabled={!isAdmin}
                value={draft.gpu_budget}
                onChange={(event) => setDraft({ ...draft, gpu_budget: event.target.value })}
                hint="Share of the card vLLM may use in total, 0.10–0.95. Leave room for the embedding encoders."
              />
            </div>
            <Select
              label="DEFAULT WEIGHTS"
              value={draft.default_weights}
              disabled={!isAdmin}
              onChange={(event) => setDraft({ ...draft, default_weights: event.target.value })}
              options={DEFAULT_WEIGHT_OPTIONS.map((option) => ({
                value: option.value,
                label: option.label,
              }))}
              hint={`${weightsHint || ''} A version can override this in Model versions → Edit metadata.`}
            />
            <div className="small muted">
              Each model gets <strong>{percent(sharePerModel)}</strong> of the GPU
              {gpuTotalGb
                ? ` (≈ ${(sharePerModel * gpuTotalGb).toFixed(1)} GB of ${gpuTotalGb.toFixed(0)} GB)`
                : ''}
              . Changes apply to the next load; models already running keep their share until they
              are reloaded.
            </div>
            <div className="row">
              <span className="xs subtle">
                {status.policy?.source === 'saved'
                  ? `Last changed by ${status.policy.updated_by || 'unknown'} ${relativeTime(status.policy.updated_at)}`
                  : 'From the environment defaults (SERVING_* in .env) — not changed here yet.'}
              </span>
              <div className="grow" />
              {isAdmin && (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={savePolicy}
                  loading={saving}
                  disabled={!dirty}
                >
                  Save policy
                </Button>
              )}
            </div>
          </div>
        )}
      </section>

      {/* ------------------------------------------------------------- loaded */}
      <section className="card card--pad">
        <div className="row" style={{ marginBottom: 10 }}>
          <div className="section-label">Loaded base models</div>
          <div className="grow" />
          <span className="xs muted">
            {budget.slots_used ?? 0} of {budget.slots ?? 1} slot{budget.slots === 1 ? '' : 's'} ·{' '}
            {percent(budget.used)} of {percent(budget.gpu_budget)} budget
          </span>
        </div>
        {servers.length === 0 ? (
          <div className="small muted">
            Nothing is loaded. Generation requests are refused with “model not loaded” until an
            administrator loads a version below.
          </div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Base model</th>
                <th>Weights</th>
                <th>GPU</th>
                <th>State</th>
                <th>Activity</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {servers.map((server) => (
                <tr key={server.key}>
                  <td>
                    <div>{server.model_source || server.model || server.key}</div>
                    <div className="xs subtle mono">{server.key}</div>
                    {server.desired && server.requested_by && (
                      <div className="xs subtle">
                        {server.version_id ? `for ${server.version_id} · ` : ''}by{' '}
                        {server.requested_by} {relativeTime(server.requested_at)}
                      </div>
                    )}
                  </td>
                  <td>{weightsLabel(server.weights_mode)}</td>
                  <td>{percent(server.gpu_fraction)}</td>
                  <td>
                    <Badge tone={STATE_TONES[server.state] || 'default'}>{server.state}</Badge>
                    {!server.desired && (
                      <div className="xs" style={{ color: 'var(--state-warn)', marginTop: 4 }}>
                        Not requested by anyone — unload it.
                      </div>
                    )}
                    {server.phase && server.state !== 'ready' && (
                      <div className="xs subtle">{server.phase}</div>
                    )}
                    {server.error && (
                      <div className="xs" style={{ color: 'var(--danger)', marginTop: 4 }}>
                        {server.error}
                      </div>
                    )}
                    {server.log_tail?.length > 0 && (
                      <details className="xs" style={{ marginTop: 4 }}>
                        <summary>vLLM log ({server.log_tail.length} lines)</summary>
                        <pre className="mono serving__code serving__log">
                          {server.log_tail.join('\n')}
                        </pre>
                      </details>
                    )}
                  </td>
                  <td className="xs">
                    {server.state === 'ready' ? (
                      <>
                        {server.stats?.running ?? 0} running · {server.stats?.waiting ?? 0} queued
                        <br />
                        KV cache {percent(server.stats?.kv_cache_usage)}
                        {server.stats?.prefix_cache_hit_rate !== undefined &&
                          ` · prefix hits ${percent(server.stats.prefix_cache_hit_rate)}`}
                      </>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td>
                    {isAdmin && server.state !== 'stopped' && (
                      <Button
                        size="sm"
                        variant="ghost"
                        loading={unloading === server.key}
                        disabled={server.state === 'draining'}
                        onClick={() => setConfirmUnload(server)}
                      >
                        Unload
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/* --------------------------------------------------------------- load */}
      {isAdmin && (
        <section className="card card--pad">
          <div className="section-label" style={{ marginBottom: 10 }}>
            Load a model
          </div>
          <div className="editor__row" style={{ alignItems: 'end' }}>
            <Select
              label="VERSION"
              value={loadVersionId}
              onChange={(event) => {
                setLoadVersionId(event.target.value);
                setPreparation(null);
              }}
              placeholder="Choose the version whose base model to load"
              options={readyVersions.map((version) => ({
                value: version.id,
                label: `${version.label || version.id}${
                  version.serving?.state === 'ready' ? ' (serving)' : ''
                }`,
              }))}
            />
            <div className="row">
              <Button
                variant="primary"
                onClick={loadModel}
                loading={loadingModel}
                disabled={!loadVersionId || !reachable}
              >
                Load
              </Button>
            </div>
          </div>
          {selectedVersion?.serving && (
            <div className="small muted" style={{ marginTop: 8 }}>
              Needs <span className="mono">{selectedVersion.serving.model_source}</span> as{' '}
              {weightsLabel(selectedVersion.serving.weights_mode)}
              {selectedVersion.serving.prepared === false && ' — not prepared on this host yet'}
              {!status.policy?.multi_model &&
                loadedDesired.length > 0 &&
                !loadedDesired.some(
                  (server) => server.key === selectedVersion.serving.server_key,
                ) &&
                `. Single-model serving: this replaces ${loadedDesired
                  .map((server) => server.model_source)
                  .join(', ')}.`}
              {selectedVersion.base_only ? '' : ' Its adapter is registered on first use.'}
            </div>
          )}
          {preparation && (
            <div className="card card--pad serving__prepare">
              <strong>Prepare this version first</strong>
              <div className="small muted" style={{ marginTop: 4 }}>
                NF4-faithful serving needs a one-off conversion of the base model on the server (a
                few minutes for an 8B model). Run, then press Load again:
              </div>
              <pre className="mono serving__code">{preparation.details?.command}</pre>
              <CopyButton value={preparation.details?.command || ''} label="Copy command" />
            </div>
          )}
        </section>
      )}

      {/* ---------------------------------------------------- GPU, admission */}
      <section className="card card--pad">
        <div className="section-label" style={{ marginBottom: 8 }}>
          GPU and admission
        </div>
        <div className="kv">
          <span className="kv__key">GPU</span>
          <span className="kv__value">{gpu.name || '—'}</span>
        </div>
        {gpu.memory_total_mb > 0 && (
          <div className="kv">
            <span className="kv__key">Memory</span>
            <span className="kv__value" style={{ minWidth: 220 }}>
              {(gpu.memory_used_mb / 1024).toFixed(1)} / {(gpu.memory_total_mb / 1024).toFixed(1)}{' '}
              GB
              <ProgressBar
                value={gpu.memory_used_mb}
                max={gpu.memory_total_mb}
                danger={gpu.memory_used_mb / gpu.memory_total_mb > 0.95}
              />
            </span>
          </div>
        )}
        <div className="kv">
          <span className="kv__key">Requests in flight</span>
          <span className="kv__value">
            {admission.in_use ?? 0} of {admission.capacity ?? '—'} slots · {admission.waiting ?? 0}{' '}
            waiting (this API process)
          </span>
        </div>
        <div className="kv">
          <span className="kv__key">Since start</span>
          <span className="kv__value">
            {admission.admitted ?? 0} admitted · {admission.rejected ?? 0} turned away after{' '}
            {admission.timeout_s ?? '—'} s
          </span>
        </div>
        <div className="kv">
          <span className="kv__key">Per organization</span>
          <span className="kv__value">
            at most {admission.organization_limit ?? '—'} slots at once
          </span>
        </div>
      </section>

      <Modal
        open={Boolean(confirmUnload)}
        onClose={() => setConfirmUnload(null)}
        title="Unload this base model?"
        subtitle={confirmUnload?.model_source || confirmUnload?.key}
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirmUnload(null)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={() => unload(confirmUnload)}>
              Unload
            </Button>
          </>
        }
      >
        <div className="small">
          Every version on this base model stops being available to everyone until it is loaded
          again. Requests already running finish first (up to a minute).
        </div>
      </Modal>
    </div>
  );
}
